import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MessageSquare, Ticket, Calendar, Wrench, Search, ShieldCheck, Clock, Headset, BookOpen, Video, ArrowRight, ChevronDown } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import StickyEmergencyBar from "@/components/support/StickyEmergencyBar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { WIZARDS, KB_ARTICLES, FAQS, SUPPORT_PHONE, SUPPORT_PHONE_TEL, SUPPORT_EMAIL, SUPPORT_DIRECTOR, BOOKING_URL } from "@/lib/supportContent";
import PublicTicketForm from "@/components/support/PublicTicketForm";

const TRUST_ITEMS = [
  { icon: Clock, label: "24/7 Support" },
  { icon: ShieldCheck, label: "Certified PUDU Technicians" },
  { icon: Headset, label: "Remote Troubleshooting" },
  { icon: Wrench, label: "On-Site Service" },
  { icon: BookOpen, label: "AI Software Specialists" },
];

const Support = () => {
  const [query, setQuery] = useState("");

  const filteredWizards = useMemo(() => {
    if (!query.trim()) return WIZARDS;
    const q = query.toLowerCase();
    return WIZARDS.filter(w =>
      w.robot.toLowerCase().includes(q) ||
      w.issue.toLowerCase().includes(q) ||
      w.steps.some(s => s.title.toLowerCase().includes(q) || s.body.toLowerCase().includes(q))
    );
  }, [query]);

  const filteredKB = useMemo(() => {
    if (!query.trim()) return KB_ARTICLES;
    const q = query.toLowerCase();
    return KB_ARTICLES.filter(a =>
      a.title.toLowerCase().includes(q) ||
      a.excerpt.toLowerCase().includes(q) ||
      a.body.toLowerCase().includes(q)
    );
  }, [query]);

  return (
    <>
      <SEO
        title="24/7 AI & Robotics Support | Neil AI Global Solutions"
        description="Enterprise-grade support for PUDU robots, AI Employees, voice agents, CRM automation, and deployment. Talk to our team 24/7 at (336) 203-3184."
        canonical="https://www.neilai.ai/support"
      />
      <div className="min-h-screen bg-[#05070A] text-white pb-24">
        <Navigation />

        {/* HERO */}
        <section className="relative pt-32 md:pt-40 pb-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A1828] via-[#05070A] to-[#0A1828]" />
          <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_30%_20%,rgba(0,245,160,0.15),transparent_50%),radial-gradient(circle_at_70%_80%,rgba(0,217,245,0.15),transparent_50%)]" />
          <div className="relative max-w-5xl mx-auto px-4 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/30 mb-6">
              <span className="w-2 h-2 rounded-full bg-[#00F5A0] animate-pulse" />
              <span className="text-xs uppercase tracking-wider text-primary font-semibold">Customer Success Center</span>
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 bg-gradient-to-r from-[#00F5A0] via-[#00D9F5] to-[#00F5A0] bg-clip-text text-transparent leading-tight">
              SUPPORT THAT NEVER SLEEPS.
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-10 leading-relaxed">
              Whether you need help with AI Employees, voice agents, cleaning robots, service robots, mapping, training, or deployment — the Neil AI Customer Success Team is available 24/7 to keep your business running.
            </p>
            <div className="flex flex-wrap gap-3 justify-center mb-10">
              <Button asChild size="lg" className="bg-gradient-to-r from-[#00F5A0] to-[#00D9F5] text-[#05070A] font-bold h-14 px-7">
                <a href={SUPPORT_PHONE_TEL}><Phone className="w-5 h-5 mr-2" />Call {SUPPORT_PHONE}</a>
              </Button>
              <Button asChild size="lg" variant="outline" className="h-14 px-7 border-primary/40">
                <Link to="/support/ticket"><Ticket className="w-5 h-5 mr-2" />Open a Ticket</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="h-14 px-7 border-primary/40">
                <Link to="/support/ai-assistant"><MessageSquare className="w-5 h-5 mr-2" />AI Support Assistant</Link>
              </Button>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-3 justify-center text-sm text-muted-foreground">
              {TRUST_ITEMS.map((it, i) => (
                <span key={i} className="inline-flex items-center gap-2">
                  <it.icon className="w-4 h-4 text-[#00F5A0]" /> {it.label}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* SEARCH */}
        <section className="py-12 bg-[#0A1828]/50 border-y border-primary/10">
          <div className="max-w-4xl mx-auto px-4">
            <div className="relative">
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search robots, errors, troubleshooting, manuals…"
                className="h-16 pl-14 pr-6 text-lg bg-[#05070A] border-primary/30 rounded-2xl"
              />
            </div>
            <p className="text-center text-xs text-muted-foreground mt-3">
              {query ? `Found ${filteredWizards.length} wizards, ${filteredKB.length} articles` : "Try: 'cc1 won't start', 'wifi', 'voice agent', 'cancel'"}
            </p>
          </div>
        </section>

        {/* DIAGNOSTIC WIZARDS */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-3">Troubleshooting Wizards</h2>
              <p className="text-muted-foreground">Step-by-step diagnostics — most issues resolved in under 5 minutes.</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredWizards.map((w) => (
                <Link key={w.slug} to={`/support/troubleshoot/${w.slug}`} className="group">
                  <Card className="h-full bg-[#0A1828]/60 border-primary/20 hover:border-primary/50 hover:shadow-[0_0_30px_rgba(0,245,160,0.15)] transition-all">
                    <CardHeader>
                      <div className="text-xs uppercase tracking-wider text-primary mb-2">{w.robot}</div>
                      <CardTitle className="text-lg group-hover:text-[#00F5A0] transition-colors">{w.issue}</CardTitle>
                      <CardDescription>{w.steps.length} guided step{w.steps.length === 1 ? "" : "s"} · {w.category === "robotics" ? "Robotics" : "AI Software"}</CardDescription>
                    </CardHeader>
                    <CardContent className="flex items-center text-sm text-primary">
                      Start wizard <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* KNOWLEDGE BASE */}
        <section className="py-16 bg-[#0A1828]/30">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-2">Knowledge Base</h2>
                <p className="text-muted-foreground">Guides, deployment info, and how-tos.</p>
              </div>
              <Button asChild variant="outline" className="border-primary/40">
                <Link to="/support/academy"><Video className="w-4 h-4 mr-2" />Video Training Academy</Link>
              </Button>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              {filteredKB.map((a) => (
                <Link key={a.slug} to={`/support/kb/${a.slug}`} className="group">
                  <Card className="h-full bg-[#0A1828]/60 border-primary/20 hover:border-primary/50 transition-all">
                    <CardHeader>
                      <div className="text-xs uppercase tracking-wider text-primary mb-2">{a.category}</div>
                      <CardTitle className="text-lg group-hover:text-[#00F5A0] transition-colors">{a.title}</CardTitle>
                      <CardDescription>{a.excerpt}</CardDescription>
                    </CardHeader>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* OPEN A TICKET (no login required) */}
        <section id="open-ticket" className="py-16 bg-[#0A1828]/40 border-y border-primary/10 scroll-mt-32">
          <div className="max-w-3xl mx-auto px-4">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/30 mb-4">
                <Ticket className="w-3.5 h-3.5 text-primary" />
                <span className="text-xs uppercase tracking-wider text-primary font-semibold">No account needed</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-3">Open a Support Ticket</h2>
              <p className="text-muted-foreground">Tell us what's happening. A specialist responds within 1 business hour — faster for urgent issues.</p>
            </div>
            <Card className="bg-[#0A1828]/60 border-primary/20 p-6 md:p-8">
              <PublicTicketForm />
            </Card>
          </div>
        </section>

        {/* FAQS */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold mb-10 text-center">Frequently Asked</h2>
            <Accordion type="single" collapsible className="space-y-3">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="bg-[#0A1828]/60 border border-primary/20 rounded-xl px-5">
                  <AccordionTrigger className="text-left text-base font-semibold">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* SUCCESS TEAM */}
        <section className="py-16 bg-[#0A1828]/40">
          <div className="max-w-5xl mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-3">Customer Success Team</h2>
            <p className="text-muted-foreground mb-12">Humans who answer the phone. Always.</p>
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="bg-[#0A1828]/60 border-primary/20 p-6">
                <Headset className="w-10 h-10 text-[#00F5A0] mx-auto mb-3" />
                <h3 className="font-bold mb-1">{SUPPORT_DIRECTOR}</h3>
                <p className="text-xs uppercase tracking-wider text-primary mb-2">Chief of Staff & Customer Success Director</p>
                <p className="text-sm text-muted-foreground">Customer support · deployment coordination · training · technical assistance</p>
              </Card>
              <Card className="bg-[#0A1828]/60 border-primary/20 p-6">
                <Wrench className="w-10 h-10 text-[#00F5A0] mx-auto mb-3" />
                <h3 className="font-bold mb-1">Certified PUDU Technicians</h3>
                <p className="text-xs uppercase tracking-wider text-primary mb-2">Field Service</p>
                <p className="text-sm text-muted-foreground">Deployment · mapping · maintenance · repairs · software updates</p>
              </Card>
              <Card className="bg-[#0A1828]/60 border-primary/20 p-6">
                <Headset className="w-10 h-10 text-[#00F5A0] mx-auto mb-3" />
                <h3 className="font-bold mb-1">AI Automation Specialists</h3>
                <p className="text-xs uppercase tracking-wider text-primary mb-2">Software Team</p>
                <p className="text-sm text-muted-foreground">AI Employees · voice agents · CRM · automation systems</p>
              </Card>
            </div>
          </div>
        </section>

        {/* ENTERPRISE CTA */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-[#FFD700] to-[#FFA500] bg-clip-text text-transparent">
              YOUR SUCCESS IS OUR MISSION.
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-10">
              When you invest in Neil AI, you gain more than technology — you gain a team committed to helping your organization automate, elevate, and dominate.
            </p>
            <div className="flex flex-wrap gap-3 justify-center mb-8">
              <Button asChild size="lg" className="bg-gradient-to-r from-[#00F5A0] to-[#00D9F5] text-[#05070A] font-bold h-14 px-7">
                <a href={SUPPORT_PHONE_TEL}><Phone className="w-5 h-5 mr-2" />Call Support Now</a>
              </Button>
              <Button asChild size="lg" variant="outline" className="h-14 px-7 border-primary/40">
                <Link to="/support/ticket"><Ticket className="w-5 h-5 mr-2" />Open a Ticket</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="h-14 px-7 border-primary/40">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer"><Calendar className="w-5 h-5 mr-2" />Book a Success Review</a>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              24/7 Support · <a href={SUPPORT_PHONE_TEL} className="text-primary hover:underline">{SUPPORT_PHONE}</a> · <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary hover:underline">{SUPPORT_EMAIL}</a>
            </p>
          </div>
        </section>

        <Footer />
        <StickyEmergencyBar />
      </div>
    </>
  );
};

export default Support;
